import { useState } from "react";
import PropTypes from "prop-types";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL_PHOTO } from "../../HelperAxios/const";
import StrinLocalized from "../../Language/language.lang";
import {
  accountPictureSrc,
  accountTitle,
  activeUserId,
  ensureActiveAccountListed,
  readSavedAccounts,
  signOutActiveAccount,
  switchToAccount,
} from "../../Utils/auth/savedAccounts";

const AccountSwitcher = ({ picture, className }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const activeId = activeUserId();

  const openMenu = (event) => {
    ensureActiveAccountListed();
    setAccounts(readSavedAccounts());
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    if (!busy) setAnchorEl(null);
  };

  const onSwitch = async (account) => {
    if (String(account.id) === String(activeId)) {
      setAnchorEl(null);
      return;
    }
    setBusy(true);
    try {
      await switchToAccount(account);
    } catch (error) {
      setBusy(false);
      setAccounts(readSavedAccounts());
      const status = error?.response?.status;
      toast.error(
        status === 401 || status === 403
          ? StrinLocalized.AccountUnavailable
          : StrinLocalized.AccountSwitchFailed
      );
    }
  };

  return (
    <>
      <IconButton onClick={openMenu} aria-label={StrinLocalized.SavedAccounts}>
        <Avatar
          className={className}
          src={accountPictureSrc(picture, API_URL_PHOTO) || undefined}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Typography
          variant="caption"
          sx={{ display: "block", px: 2, py: 1, opacity: 0.7 }}
        >
          {StrinLocalized.SavedAccounts}
        </Typography>
        {accounts.map((account) => {
          const selected = String(account.id) === String(activeId);
          return (
            <MenuItem
              key={account.id}
              disabled={busy}
              selected={selected}
              onClick={() => onSwitch(account)}
              sx={{ minWidth: 260, gap: 1 }}
            >
              <Avatar
                src={accountPictureSrc(account.picture, API_URL_PHOTO) || undefined}
                sx={{ width: 32, height: 32 }}
              >
                {(accountTitle(account) || "?").slice(0, 1)}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap>
                  {accountTitle(account)}
                </Typography>
                {account.username && (
                  <Typography variant="caption" noWrap sx={{ opacity: 0.7 }}>
                    {account.username}
                  </Typography>
                )}
              </Box>
              {selected && <CheckIcon fontSize="small" />}
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem
          disabled={busy}
          onClick={() => navigate("/login?addAccount=1")}
        >
          <ListItemIcon>
            <PersonAddAltIcon fontSize="small" />
          </ListItemIcon>
          {StrinLocalized.AddAccount}
        </MenuItem>
        <MenuItem disabled={busy} onClick={() => navigate("/my-profile")}>
          {StrinLocalized.Profile}
        </MenuItem>
        <MenuItem
          disabled={busy}
          onClick={() => {
            setBusy(true);
            signOutActiveAccount();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {StrinLocalized.Logout}
        </MenuItem>
      </Menu>
    </>
  );
};

AccountSwitcher.propTypes = {
  picture: PropTypes.string,
  className: PropTypes.string,
};

export default AccountSwitcher;
